import { isEmpty } from 'lodash';
import { VALUATIONS_OTHER_LABEL } from 'common/constants/valuations';
import {
  LIMITED_VALUES_OPTIONS,
  SINGLE_COMPARISON_OPTIONS,
  SPECIFIED_LABEL,
  VALUES_OPTIONS,
  VALUES_OPTIONS_WITHOUT_SPECIFIED,
  WEIGHTED_LABEL,
} from 'pages/Valuations/approaches/guidelinePublicCompanies/constants';
import { getPercentileLabel } from 'pages/ValuationsAllocation/util/getPercentileLabel';
import { extractPercentileNumber, getObjectValue } from 'utilities';
import { BenchmarkApproach, GetOptionsParams, GetSelectionCellOptionsParams } from './types';

const getOptions = (params: GetOptionsParams) => {
  const { options, shouldIgnorePercentiles, shouldIgnoreSpecified, isSingleComparison } = params;

  if (shouldIgnorePercentiles) {
    return [...LIMITED_VALUES_OPTIONS];
  }

  if (shouldIgnoreSpecified) {
    return [...VALUES_OPTIONS_WITHOUT_SPECIFIED];
  }

  if (isSingleComparison) {
    return [...SINGLE_COMPARISON_OPTIONS];
  }

  return [...options];
};

const getSelectionCellOptions = (params: GetSelectionCellOptionsParams) => {
  const {
    isDCF = false,
    isMarketAdjustment = false,
    isSingleComparison = false,
    shouldIgnorePercentiles = false,
    shouldIgnoreSpecified = false,
    specificApproach,
  } = params;

  const options = getOptions({
    isSingleComparison,
    options: VALUES_OPTIONS,
    shouldIgnorePercentiles,
    shouldIgnoreSpecified,
  });

  if (isEmpty(specificApproach)) {
    return isDCF ? ['Other'] : options;
  }

  const { percentile_selection_a: percentileSelectionA, percentile_selection_b: percentileSelectionB } = getObjectValue(
    specificApproach as BenchmarkApproach
  );

  if (!isSingleComparison && percentileSelectionA && percentileSelectionA !== 75) {
    options.push(getPercentileLabel(percentileSelectionA - 1));
  }

  if (!isSingleComparison && percentileSelectionB && percentileSelectionB !== 25) {
    options.push(getPercentileLabel(percentileSelectionB - 1));
  }

  if (options.length > 5) {
    const [firstOption, secondOption, ...restOptions] = options;

    const percentileOptions = restOptions.filter(option => option !== SPECIFIED_LABEL);

    percentileOptions.sort((a, b) => extractPercentileNumber(a) - extractPercentileNumber(b));

    const uniqueOptions = new Set([firstOption, secondOption, ...percentileOptions, SPECIFIED_LABEL]);

    if (isDCF) {
      return [...uniqueOptions, VALUATIONS_OTHER_LABEL];
    }

    if (isMarketAdjustment) {
      return [...uniqueOptions, WEIGHTED_LABEL];
    }

    return [...uniqueOptions];
  }

  if (isMarketAdjustment) {
    options.push(WEIGHTED_LABEL);
  }

  if (isDCF) {
    options.push(VALUATIONS_OTHER_LABEL);
  }

  return options;
};

export default getSelectionCellOptions;
